import {useNuxtApp} from 'nuxt/app'
import {useUserStore} from '@/store/user'
import {useAuthStore} from '@/store/auth'
import {useCartStore} from '@/store/cart'
import {usePlantStore} from '@/store/plants'
import {useCustomerSettingsStore} from '@/store/customer-settings'
import {useCustomerService} from '@/composables/customer_service'
import {useKeycloakService} from '~/composables/keycloak_service'
import type {UserManager} from 'oidc-client-ts'
import {getRegionForSalesArea} from '~/assets/js/utils/region_service'
import {DefaultCustomerUuid, MAX_CUSTOMERS} from '~/assets/js/constants'
import {useGtm} from '@gtm-support/vue-gtm'
import * as Sentry from '@sentry/browser'
import {decodeKeycloakToken, restoreUserStoreFromAccessToken, buildKeycloakUser} from './useKeycloakToken'
import {clearKeycloakState} from './useKeycloakSession'

async function processKeycloakUserData(
  accessToken: string,
  gtmAction: string = 'login'
) {
  const app = useNuxtApp()
  const userStore = useUserStore()
  const customerService = useCustomerService()

  const decodedToken = decodeKeycloakToken(accessToken)

  const region = app.$globalization.getRegionFromURL()
  const customerRegion = decodedToken.salesAreaName
    ? getRegionForSalesArea(decodedToken.salesAreaName.replaceAll('_', '-'))
    : null
  app.$globalization.setRegion(customerRegion)

  if (region && customerRegion && region !== customerRegion) {
    const targetUrl = app.$globalization.localePath('/', null, customerRegion)
    globalThis.location.replace(targetUrl)
    return
  }

  userStore.fetchUserData()
  userStore.parseUserRights()
  userStore.parseExcludedBrands()
  userStore.parseIncludedBrands()

  const defaultcustomerUuid = decodedToken.iss
  const tokenScope = decodedToken.scope ?? ''

  useGtm()?.trackEvent({
    event: 'login',
    category: 'login',
    action: gtmAction,
    companyNo: decodedToken?.companyNo,
    salesAreaName: decodedToken?.salesAreaName,
    userType: decodedToken?.userIsInternal === 'true' ? 'internal' : 'external',
    authProvider: 'keycloak'
  })

  if (defaultcustomerUuid && defaultcustomerUuid !== DefaultCustomerUuid) {
    userStore.companiesLoading = true
    let defaultCustomer = null
    if (tokenScope.split(' ').includes(`https://sso.fst.com/${defaultcustomerUuid}/Customer.View`)) {
      defaultCustomer = await customerService.getCustomer(defaultcustomerUuid.split('/')[3], accessToken)
    } else {
      defaultCustomer = await customerService.getBasicCustomer(defaultcustomerUuid.split('/')[3], accessToken)
    }
    await userStore.setSelectedCustomer(defaultCustomer?.value.find(Boolean))
    const userCustomerSettingsStore = useCustomerSettingsStore()
    await userCustomerSettingsStore.fetchCustomerSettings()
    customerService.getFilteredCustomerList(undefined, MAX_CUSTOMERS).then((companies) => {
      userStore.setAvailableCompanies(companies.count)
      userStore.setCompanies(companies.value)
      userStore.companiesLoading = false
    })
    usePlantStore().fetchAllPlants(false)
  }

  const cartStore = useCartStore()
  cartStore.fetchCartCount()
  cartStore.fetchCertificates()
}

async function fetchKeycloakUser(userManager: UserManager) {
  const userStore = useUserStore()
  const authStore = useAuthStore()
  authStore.userFetchInProgress = true

  const app = useNuxtApp()

  try {
    const accessToken = userStore.accessToken
    if (!accessToken) {
      Sentry.captureMessage('No access token available for Keycloak user fetch', 'warning')
      return
    }

    await processKeycloakUserData(accessToken, 'login')
  } catch (e: any) {
    if (e?.response?.data?.errorCode === 'Customer_Uuid_NotExisting' || e?.response?.data?.errorCode === 'Unauthorized') {
      await clearKeycloakState(userManager)
      const baseUrl = useRuntimeConfig().public.BASE_URL
      const accessDeniedPath = baseUrl + app.localePath('/access-denied', null, null)
      globalThis.location.replace(accessDeniedPath)
      return
    }

    await clearKeycloakState(userManager)
    Sentry.captureException(e, {tags: {source: 'keycloak_user_fetch'}})
  } finally {
    authStore.userFetchInProgress = false
  }
}

async function switchKeycloakCustomer(newCustomerUuid: string, gtmAction: string = 'change_company') {
  if (!process.client) {return}

  const app = useNuxtApp()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const {switchKeycloakCustomer: backendSwitch} = useKeycloakService()

  authStore.userFetchInProgress = true

  try {
    const currentAccessToken = userStore.accessToken
    if (!currentAccessToken) {
      Sentry.captureMessage('No access token available for customer switch', 'warning')
      return
    }

    // Call backend to get new token with new customer context
    const res = await backendSwitch(newCustomerUuid, currentAccessToken)

    if (res?.data?.accessToken) {
      const newAccessToken = res.data.accessToken

      restoreUserStoreFromAccessToken(newAccessToken)

      const existingUser = await app.$userManager.getUser()
      const updatedUser = buildKeycloakUser(
        newAccessToken,
        res.data.idToken ?? existingUser?.id_token,
        res.data.refreshToken ?? existingUser?.refresh_token,
        existingUser?.state
      )

      await app.$userManager.storeUser(updatedUser)
      authStore.setUserObject(updatedUser)

      await processKeycloakUserData(newAccessToken, gtmAction)
    }
  } catch (error: any) {
    Sentry.captureException(error, {tags: {source: 'keycloak_customer_switch'}})
    throw error
  } finally {
    authStore.userFetchInProgress = false
  }
}

export const useKeycloakUser = () => {
  return {
    fetchKeycloakUser,
    switchKeycloakCustomer
  }
}
